import {
  IngredientRecipeSubgraphResult,
  IngredientRecipeSubgraphNode,
} from '../../api/types.js';
import { unitCache } from '../../unit-cache.js';
import {
  buildSubgraphFooterHtml,
  buildSubgraphIngredientsHtml,
  buildSubgraphPreparationHtml,
} from '../../explorer/subgraphElaboration.js';

/**
 * Modal dialog that displays ingredient recipe subgraph search results and
 * lets the user pick one to import into their current recipe.
 *
 * Shows exactly one candidate elaboration at a time — the same
 * Ingredients + Preparació framed view as the ingredient explorer's own
 * "Elaboracions" section (subgraphElaboration.ts, shared so the two never
 * drift apart) — with ‹/› paging once there's more than one, rather than a
 * scrollable list of summary cards.
 */
export class IngredientRecipeSelector {
  private subgraphs: IngredientRecipeSubgraphResult[];
  private ingredientName: string;
  private onSelect: (nodes: Record<string, IngredientRecipeSubgraphNode>) => void;
  private index = 0;

  constructor(
    subgraphs: IngredientRecipeSubgraphResult[],
    ingredientName: string,
    onSelect: (nodes: Record<string, IngredientRecipeSubgraphNode>) => void,
  ) {
    this.subgraphs = subgraphs;
    this.ingredientName = ingredientName;
    this.onSelect = onSelect;
  }

  /**
   * Open the modal and return when the user selects one (or cancels).
   * Returns true if a selection was made, false if cancelled.
   */
  async open(): Promise<boolean> {
    // Quantities in the elaboration view need unit display names — cheap
    // no-op if something else already loaded the cache (normally true
    // here, since this only ever opens from within a recipe page, which
    // preloads it itself).
    await unitCache.ensureLoaded().catch(() => {});

    return new Promise((resolve) => {
      // Create backdrop overlay
      const overlay = document.createElement('div');
      overlay.className =
        'fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm';
      overlay.style.animation = 'fadeIn 150ms ease-out';

      // Dialog container
      const dialog = document.createElement('div');
      dialog.className =
        'bg-[#1a1a2e] border border-[var(--accent-alpha-27)] rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden';
      dialog.style.animation = 'slideUp 200ms ease-out';

      // ── Header ──
      const header = document.createElement('div');
      header.className =
        'flex items-center justify-between px-6 py-4 border-b border-[var(--accent-alpha-20)]';

      const title = document.createElement('h2');
      title.className = 'text-lg font-bold text-[var(--accent)] font-mono';
      title.textContent = `Receptes per a "${this.ingredientName}"`;
      header.appendChild(title);

      // Tick/cross — same circular icon-button language as EditToolbar/
      // ExplorerPage's own save+cancel pair. Tick selects whichever
      // elaboration is currently shown (replaces the old "Selecciona
      // aquesta elaboració" bar); cross cancels (replaces both the old ×
      // and the footer's "Cancel·la" button). Reading this.index fresh at
      // click time, not captured here, so paging (renderBody rebuilding
      // the body below) doesn't go stale.
      const headerActions = document.createElement('div');
      headerActions.className = 'flex items-center gap-2';

      // No tick at all when there's nothing to select — same as the old
      // design, where the "Selecciona aquesta elaboració" bar only ever
      // rendered once there was at least one result.
      if (this.subgraphs.length > 0) {
        const selectBtn = this.buildIconButton('✓', 'Selecciona aquesta elaboració', true);
        selectBtn.addEventListener('click', () => {
          this.onSelect(this.subgraphs[this.index].nodes);
          this.closeOverlay();
          resolve(true);
        });
        headerActions.appendChild(selectBtn);
      }

      const cancelBtn = this.buildIconButton('✕', 'Cancel·la', false);
      cancelBtn.addEventListener('click', () => {
        this.closeOverlay();
        resolve(false);
      });
      headerActions.appendChild(cancelBtn);

      header.appendChild(headerActions);

      dialog.appendChild(header);

      // ── Body ──
      const body = document.createElement('div');
      body.className = 'flex-1 overflow-y-auto p-4 space-y-3';
      dialog.appendChild(body);

      this.renderBody(body);

      overlay.appendChild(dialog);
      document.body.appendChild(overlay);

      // Close on overlay click (not dialog click)
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closeOverlay();
          resolve(false);
        }
      });

      // Close on Escape
      const keyHandler = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          this.closeOverlay();
          resolve(false);
          document.removeEventListener('keydown', keyHandler);
        }
      };
      document.addEventListener('keydown', keyHandler);

      // Focus trap
      dialog.focus();
    });
  }

  private renderBody(body: HTMLElement): void {
    body.innerHTML = '';

    if (this.subgraphs.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'text-center py-12 text-[#888] font-mono';
      empty.innerHTML = `
        <div class="text-lg mb-2">No s'han trobat receptes</div>
        <div class="text-sm">No hi ha cap subgraf que utilitzi aquest ingredient</div>
      `;
      body.appendChild(empty);
      return;
    }

    const sg = this.subgraphs[this.index];

    // Same framed Ingredients + Preparació view as the ingredient
    // explorer's own "Elaboracions" section, with the same recipe-name
    // footer underneath.
    const view = document.createElement('div');
    view.className = 'border border-[var(--accent-alpha-20)] rounded-lg bg-[#151525] overflow-hidden';

    const content = document.createElement('div');
    content.className = 'flex flex-col sm:flex-row gap-4 p-4';
    content.appendChild(this.buildColumn('Ingredients', buildSubgraphIngredientsHtml(sg.nodes)));
    content.appendChild(this.buildColumn('Preparació', buildSubgraphPreparationHtml(sg.nodes)));
    view.appendChild(content);

    const footer = document.createElement('div');
    footer.className = 'px-4 py-2 border-t border-[var(--accent-alpha-20)]';
    footer.innerHTML = buildSubgraphFooterHtml(sg);
    view.appendChild(footer);

    body.appendChild(view);

    if (this.subgraphs.length > 1) {
      const pager = document.createElement('div');
      pager.className = 'flex items-center justify-center gap-3';
      pager.appendChild(this.buildCyclerArrow('‹', () => {
        this.index = (this.index - 1 + this.subgraphs.length) % this.subgraphs.length;
        this.renderBody(body);
      }));
      const counter = document.createElement('span');
      counter.className = 'text-xs text-[#888] font-mono';
      counter.textContent = `${this.index + 1} / ${this.subgraphs.length}`;
      pager.appendChild(counter);
      pager.appendChild(this.buildCyclerArrow('›', () => {
        this.index = (this.index + 1) % this.subgraphs.length;
        this.renderBody(body);
      }));
      body.appendChild(pager);
    }
  }

  private buildColumn(heading: string, innerHtml: string): HTMLElement {
    const col = document.createElement('div');
    col.className = 'flex-1 min-w-0 space-y-1';

    const label = document.createElement('div');
    label.className = 'text-xs font-mono uppercase tracking-wider text-[#888]';
    label.textContent = heading;
    col.appendChild(label);

    const contentEl = document.createElement('div');
    contentEl.innerHTML = innerHtml;
    col.appendChild(contentEl);

    return col;
  }

  /**
   * Circular icon button matching the app's established save/cancel
   * language (EditToolbar.makeButton / ExplorerPage.buildIconButton /
   * NodeEditPopup's own close button) — used here for the header's
   * tick (select) and cross (cancel) buttons.
   */
  private buildIconButton(icon: string, title: string, active: boolean): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.title = title;
    btn.textContent = icon;
    btn.style.cssText = `
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 15px;
      font-weight: bold;
      font-family: inherit;
      border-radius: 50%;
      border: 1px solid ${active ? 'var(--accent)' : 'var(--accent-alpha-40)'};
      background: ${active ? 'var(--accent)' : 'transparent'};
      color: ${active ? '#0d1117' : 'var(--accent)'};
      cursor: pointer;
      transition: all 0.15s ease;
      flex-shrink: 0;
    `;
    return btn;
  }

  private buildCyclerArrow(glyph: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = glyph;
    btn.className =
      'w-7 h-7 flex items-center justify-center rounded-full border border-[var(--accent-alpha-40)] text-[var(--accent)] hover:bg-[var(--accent-alpha-13)] font-mono';
    btn.addEventListener('click', onClick);
    return btn;
  }

  private closeOverlay(): void {
    const els = document.querySelectorAll('.fixed.inset-0.z-50');
    els.forEach((el) => {
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
    });
  }
}
